import React, { useState, useEffect, useRef } from 'react';
import { Bell, CheckCircle, Clock, Plus, AlertCircle, Calendar, ArrowRight, Trash2, Award, Mail, BellRing, Wifi } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import DashboardLayout from '../components/DashboardLayout';
import { reminderAPI } from '../services/apiServices';
import { toast } from '../hooks/use-toast';
import notificationService from '../services/notificationService';

const Reminders = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState('ongoing');

  const [newReminder, setNewReminder] = useState({
    title: '',
    dueDate: '',
    amount: '',
    type: 'Bill',
    priority: 'Medium'
  });

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const data = await reminderAPI.getReminders();
      if (data) setReminders(data);
    } catch (error) {
      toast({ title: 'Sync Error', description: 'Failed to synchronize reminders stream.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders();
    setNotificationsEnabled(notificationService.checkPermission());
  }, []);

  const handleAddReminder = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: newReminder.title,
        amount: parseFloat(newReminder.amount) || 0,
        due_date: newReminder.dueDate,
        type: newReminder.type,
        priority: newReminder.priority,
        is_completed: false
      };
      await reminderAPI.createReminder(payload);
      toast({ title: 'Reminder Locked', description: 'Priority task added to neural queue.' });
      setIsAddDialogOpen(false);
      setNewReminder({ title: '', dueDate: '', amount: '', type: 'Bill', priority: 'Medium' });
      fetchReminders();
    } catch (error) {
      toast({ title: 'Save Failure', description: 'Encryption error. Refresh and retry.' });
    }
  };

  const handleMarkComplete = async (id) => {
    try {
      setReminders(prev => prev.map(r => r.id === id ? { ...r, is_completed: true } : r));
      await reminderAPI.completeReminder(id);
      toast({ title: 'Task Terminated', description: 'Reminder successfully archived.' });
    } catch (error) {
      toast({ title: 'Update Error', description: 'Status reconciliation failed.' });
      fetchReminders();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await reminderAPI.deleteReminder(id);
      setReminders(prev => prev.filter(r => r.id !== id));
      toast({ title: 'Record Purged', description: 'Identity trace removed.' });
    } catch (error) {
      toast({ title: 'Deletion Error', description: 'Record lock active.' });
    }
  };

  const handleEnableNotifications = async () => {
    if (Notification.permission === 'denied') {
      toast({
        title: 'Neural Link Blocked',
        description: 'Notifications are blocked by your browser. Please reset permissions in the site settings (lock icon in URL bar) to enable alerts.',
        variant: 'destructive'
      });
      return;
    }

    const granted = await notificationService.requestPermission();
    if (granted) {
      setNotificationsEnabled(true);
      toast({ title: 'Neural Alerts Enabled', description: 'Real-time focus notifications active.' });
    } else {
      toast({ title: 'Sync Failed', description: 'Notification permission was not granted.' });
    }
  };

  const isOverdue = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  const activeReminders = reminders.filter(r => !r.is_completed);
  const completedReminders = reminders.filter(r => r.is_completed);
  const missedReminders = activeReminders.filter(r => isOverdue(r.due_date));
  const ongoingReminders = activeReminders.filter(r => !isOverdue(r.due_date));

  const TabBadge = ({ count, type }) => {
    if (count === 0 && type !== 'completed') return null;
    if (type === 'completed') return null; // Constraint: completed count should not be visible

    const colorClasses = {
      ongoing: 'bg-slate-800 text-slate-300',
      missed: 'bg-red-500/20 text-red-400 border border-red-500/30',
      completed: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
    };

    return (
      <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${colorClasses[type]}`}>
        {count}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#020617] p-4 md:p-8 font-jakarta selection:bg-slate-500/30 overflow-x-hidden text-[#F8FAFC]">
        {/* CUSTOM THEME STYLES */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
          .font-jakarta { font-family: 'Plus Jakarta Sans', sans-serif; }
          
          .glass-tile {
            background: #000000;
            border: 1px solid rgba(148, 163, 184, 0.2);
          }
          
          .silver-card {
            background: #000000;
            border: 1px solid rgba(148, 163, 184, 0.2);
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            perspective: 1200px;
            transform-style: preserve-3d;
          }
          
          .silver-card:hover {
            border-color: rgba(148, 163, 184, 0.5);
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
            transform: translateY(-4px) translateZ(10px);
          }

          .card-spotlight {
            position: absolute;
            inset: 0;
            background: radial-gradient(400px circle at var(--mouse-x) var(--mouse-y), rgba(148, 163, 184, 0.12), transparent 80%);
            pointer-events: none;
            z-index: 10;
          }

          @keyframes pulse-red {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.05); }
          }
          .animate-pulse-red { animation: pulse-red 2s infinite; }

          .pill-badge {
            background: rgba(148, 163, 184, 0.1);
            border: 1px solid rgba(148, 163, 184, 0.2);
            backdrop-filter: blur(4px);
          }
        `}</style>

        <div className="max-w-7xl mx-auto space-y-10">
          {/* Header Implementation: Portfolio Style */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
            <div className="space-y-4 animate-in fade-in slide-in-from-left-6 duration-1000">
              <div className="flex items-center gap-2 pill-badge px-3 py-1 rounded-full w-fit">
                <Wifi size={12} className="text-slate-400 animate-pulse" />
                <span className="text-[8px] font-black uppercase tracking-[0.3em] text-[#94A3B8]">SYSTEM ONLINE</span>
              </div>
              <div>
                <h1 className="text-xl md:text-3xl font-black tracking-tight uppercase leading-none flex items-baseline gap-3">
                  <span className="text-white">REMINDERS</span>
                  <span className="text-[#94A3B8] text-2xl md:text-3xl">& ALERTS</span>
                </h1>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Button
                onClick={handleEnableNotifications}
                className={`h-12 px-6 rounded-2xl font-bold border transition-all duration-500 overflow-hidden relative group shrink-0 ${notificationsEnabled
                  ? 'border-emerald-500/50 text-emerald-400 bg-emerald-500/5 hover:bg-emerald-500/10'
                  : 'border-white/10 bg-black text-slate-400 hover:border-slate-400/30'}`}
              >
                <BellRing size={18} className={`mr-2 ${notificationsEnabled ? '' : 'group-hover:animate-bounce'}`} />
                {notificationsEnabled ? 'Alerts Sync Active' : 'Activate Push Alerts'}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </Button>

              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="h-12 px-8 rounded-2xl font-black uppercase text-xs tracking-[0.2em] bg-white text-black hover:bg-slate-200 shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all hover:scale-[1.02] active:scale-95 group">
                    <Plus size={18} className="mr-2 group-hover:rotate-90 transition-transform" />
                    Set New Reminder
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md bg-black border-white/10 text-white p-8 rounded-[2rem] shadow-[0_0_50px_rgba(0,0,0,1)] perspective-1000">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-black font-jakarta tracking-tight">ENCODE REMINDER</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddReminder} className="space-y-6 pt-4">
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#94A3B8]">Identifier</Label>
                      <Input
                        placeholder="e.g., QUANTUM BILL"
                        className="bg-zinc-900/50 border-white/5 h-11 rounded-xl focus:border-slate-400 transition-all font-medium"
                        value={newReminder.title}
                        onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#94A3B8]">Category</Label>
                        <Select value={newReminder.type} onValueChange={(v) => setNewReminder({ ...newReminder, type: v })}>
                          <SelectTrigger className="bg-slate-900/50 border-white/5 rounded-xl h-11 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-950 border-white/10 text-white">
                            <SelectItem value="Bill">Bill Payment</SelectItem>
                            <SelectItem value="EMI">EMI Slot</SelectItem>
                            <SelectItem value="Investment">SIP Investment</SelectItem>
                            <SelectItem value="Subscription">Recurrent Sub</SelectItem>
                            <SelectItem value="Other">Misc Action</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#94A3B8]">Priority</Label>
                        <Select value={newReminder.priority} onValueChange={(v) => setNewReminder({ ...newReminder, priority: v })}>
                          <SelectTrigger className="bg-slate-900/50 border-white/5 rounded-xl h-12 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-950 border-white/10 text-white">
                            <SelectItem value="High">Emergency</SelectItem>
                            <SelectItem value="Medium">Standard</SelectItem>
                            <SelectItem value="Low">Low Priority</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#94A3B8]">Deadline</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full justify-start text-left font-normal bg-zinc-900/50 border-white/5 h-11 rounded-xl hover:bg-zinc-800/50",
                                !newReminder.dueDate && "text-muted-foreground"
                              )}
                            >
                              <Calendar className="mr-2 h-4 w-4" />
                              {newReminder.dueDate && !isNaN(new Date(newReminder.dueDate)) ? format(new Date(newReminder.dueDate), "PPP") : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-black border-white/10" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={newReminder.dueDate ? new Date(newReminder.dueDate) : undefined}
                              onSelect={(date) => setNewReminder({ ...newReminder, dueDate: date?.toISOString().split('T')[0] || '' })}
                              initialFocus
                              className="bg-black text-white"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#94A3B8]">Quantum (₹)</Label>
                        <Input
                          type="number"
                          placeholder="0.00"
                          className="bg-zinc-900/50 border-white/5 h-11 rounded-xl text-white"
                          value={newReminder.amount}
                          onChange={(e) => setNewReminder({ ...newReminder, amount: e.target.value })}
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-14 bg-white text-black font-black uppercase text-xs tracking-[0.3em] rounded-2xl hover:bg-slate-200 transition-all">
                      LOCK ACTION
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <Tabs defaultValue="ongoing" className="w-full" onValueChange={setActiveTab}>
            <TabsList className="bg-black border border-white/10 p-1 rounded-xl h-12 w-full max-w-xl mx-auto mb-10 overflow-x-auto custom-scrollbar">
              <TabsTrigger
                value="ongoing"
                className="flex-1 rounded-lg data-[state=active]:bg-slate-800/60 data-[state=active]:text-white text-slate-400 font-bold text-[10px] uppercase tracking-widest transition-all h-full"
              >
                Ongoing <TabBadge count={ongoingReminders.length} type="ongoing" />
              </TabsTrigger>
              <TabsTrigger
                value="missed"
                className="flex-1 rounded-lg data-[state=active]:bg-red-900/40 data-[state=active]:text-red-400 text-slate-400 font-bold text-[10px] uppercase tracking-widest transition-all h-full"
              >
                Missed <TabBadge count={missedReminders.length} type="missed" />
              </TabsTrigger>
              <TabsTrigger
                value="completed"
                className="flex-1 rounded-lg data-[state=active]:bg-emerald-900/40 data-[state=active]:text-emerald-400 text-slate-400 font-bold text-[10px] uppercase tracking-widest transition-all h-full"
              >
                Completed <TabBadge count={completedReminders.length} type="completed" />
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ongoing" className="outline-none">
              {ongoingReminders.length === 0 ? (
                <EmptyState icon={<CheckCircle size={40} className="text-slate-800" />} title="All Clear" desc="No upcoming tasks detected in neural queue." />
              ) : (
                <div className="grid gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  {ongoingReminders.map((item) => <ReminderCard key={item.id} item={item} onComplete={handleMarkComplete} onDelete={handleDelete} />)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="missed" className="outline-none">
              {missedReminders.length === 0 ? (
                <EmptyState icon={<AlertCircle size={40} className="text-slate-800" />} title="Zero Delays" desc="No missed reminders in this sector." />
              ) : (
                <div className="grid gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  {missedReminders.map((item) => <ReminderCard key={item.id} item={item} isMissed={true} onComplete={handleMarkComplete} onDelete={handleDelete} />)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="completed" className="outline-none">
              {completedReminders.length === 0 ? (
                <EmptyState icon={<Award size={40} className="text-slate-800" />} title="Empty History" desc="Launch tasks to fill your archives." />
              ) : (
                <div className="grid gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  {completedReminders.map((item) => <ReminderCard key={item.id} item={item} isCompleted={true} onDelete={handleDelete} />)}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </DashboardLayout>
  );
};

// --- SUB-COMPONENTS ---

const EmptyState = ({ icon, title, desc }) => (
  <div className="py-20 glass-tile rounded-[3rem] text-center border-dashed border-white/5 space-y-4 max-w-xl mx-auto">
    <div className="w-16 h-16 bg-slate-900/50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/5">
      {icon}
    </div>
    <h3 className="text-xl font-bold text-white uppercase tracking-tight">{title}</h3>
    <p className="text-slate-500 font-medium text-xs max-w-xs mx-auto leading-relaxed">{desc}</p>
  </div>
);

const ReminderCard = ({ item, isMissed = false, isCompleted = false, onComplete, onDelete }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -5;
    const rotateY = ((x - centerX) / centerX) * 5;

    card.style.setProperty('--mouse-x', `${x}px`);
    card.style.setProperty('--mouse-y', `${y}px`);
    card.style.transform = `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1200px) rotateX(0deg) rotateY(0deg)`;
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`silver-card group p-4 md:p-5 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden ${isMissed ? 'border-red-500/30 bg-red-500/5' :
        isCompleted ? 'opacity-70 grayscale-[0.5]' : ''
        }`}
    >
      <div className="card-spotlight"></div>

      <div className="flex items-center gap-4 relative z-20 w-full">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center border transition-all duration-500 ${isMissed ? 'bg-red-950/20 border-red-500/20 text-red-500 group-hover:bg-red-500 group-hover:text-black' :
          isCompleted ? 'bg-slate-900 border-white/5 text-slate-500' :
            'bg-slate-900 border-white/5 text-white group-hover:bg-white group-hover:text-black'
          }`}>
          {item.type === 'Bill' ? <Mail size={20} /> : <Calendar size={20} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className={`text-[15px] md:text-[16px] font-bold tracking-tight truncate ${isCompleted ? 'line-through text-slate-500' : 'text-white'}`}>
              {item.title}
            </h3>
            {!isCompleted && (
              <span className={`text-[8px] font-black uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-md border ${item.priority === 'High' ? 'bg-red-500/10 border-red-500/20 text-red-500' :
                item.priority === 'Medium' ? 'bg-orange-500/10 border-orange-500/20 text-orange-500' :
                  'bg-yellow-500/10 border-yellow-500/20 text-yellow-500'
                }`}>
                {item.priority}
              </span>
            )}
            {isMissed && (
              <span className="text-[8px] font-black uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-md bg-red-500 text-black animate-pulse-red">
                OVERDUE
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 text-[11px] md:text-[12px] font-medium text-[#94A3B8]">
            <span className="flex items-center gap-1 font-jakarta uppercase tracking-wider">
              <Clock size={12} className={isMissed ? "text-red-500" : "text-slate-500"} />
              {new Date(item.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="opacity-20">|</span>
            <span className="font-jakarta uppercase tracking-[0.1em] text-[9px]">{item.type}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-6 relative z-20 w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 border-white/5 pt-4 md:pt-0">
        <div className="text-right">
          <p className={`text-[16px] md:text-[18px] font-black font-jakarta ${isCompleted ? 'line-through text-slate-500' : 'text-white'}`}>
            ₹{Number(item.amount).toLocaleString('en-IN')}
          </p>
          <p className="text-[8px] font-black uppercase text-slate-500 tracking-widest mt-0.5">Quantum Balance</p>
        </div>

        <div className="flex items-center gap-2">
          {!isCompleted && (
            <button
              onClick={() => onComplete(item.id)}
              className="h-9 px-4 rounded-lg border-2 border-slate-700 text-slate-400 font-black text-[9px] uppercase tracking-[0.2em] hover:bg-white hover:text-black hover:border-white transition-all duration-300"
            >
              RESOLVE
            </button>
          )}
          <button
            onClick={() => onDelete(item.id)}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-600 hover:text-red-500 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all duration-300"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Reminders;
